import { useEffect, useRef, useState } from 'react';
import { Wand2, RefreshCw, Trash2, Eye, EyeOff, UploadCloud, LayoutGrid, List, Play, Square } from 'lucide-react';
import { effectsApi } from '../../lib/api';
import type { AccentEffect, EffectTier } from '../../../../shared/types';

// Minimal typing for the global injected by /accent-backgrounds.js
interface AccentBGGlobal {
  accents: { name: string; hex: string }[];
  setAccent: (hex: string) => void;
  register: (rec: Partial<AccentEffect>) => boolean;
  mount: (el: HTMLElement, key: string) => { inst?: { start?: () => void; stop?: () => void }; io?: unknown; cv?: unknown; el?: unknown; key?: string } | null;
  unmount?: (rec: unknown) => void;
  validate: (
    rec: Partial<AccentEffect>,
    opts?: { frames?: number; budgetMs?: number; width?: number }
  ) => { ok: boolean; error: string | null; avgMs: number };
}
declare global {
  interface Window { AccentBG?: AccentBGGlobal }
}

const TIERS: EffectTier[] = ['free', 'pro', 'business'];

function useAccentBG() {
  const [ready, setReady] = useState(!!window.AccentBG);
  useEffect(() => {
    if (window.AccentBG) { setReady(true); return; }
    const existing = document.querySelector<HTMLScriptElement>('script[data-accent-bg-runtime]');
    const onLoad = () => setReady(true);
    if (existing) { existing.addEventListener('load', onLoad); return () => existing.removeEventListener('load', onLoad); }
    const s = document.createElement('script');
    s.src = '/accent-backgrounds.js';
    s.async = true;
    s.dataset.accentBgRuntime = '1';
    s.addEventListener('load', onLoad);
    document.body.appendChild(s);
    return () => s.removeEventListener('load', onLoad);
  }, []);
  return ready;
}

function EffectPills({ e }: { e: AccentEffect }) {
  const animated = e.perf >= 2; // conventie: statisch effect = perf 1
  const perfColor = e.perf === 1 ? 'bg-emerald-50 text-emerald-700'
    : e.perf === 2 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700';
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${animated ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
        {animated ? <Play className="w-2.5 h-2.5" /> : <Square className="w-2.5 h-2.5" />}
        {animated ? 'Geanimeerd' : 'Statisch'}
      </span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
        e.tier === 'business' ? 'bg-purple-100 text-purple-700'
        : e.tier === 'pro' ? 'bg-amber-100 text-amber-700'
        : 'bg-gray-100 text-gray-600'}`}>
        {e.tier === 'free' ? 'free' : `vanaf ${e.tier}`}
      </span>
      <span title="Rekenkosten (1 licht – 3 zwaar)" className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${perfColor}`}>
        perf {e.perf}
      </span>
      {e.category === 'Ingebouwd' && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">Ingebouwd</span>
      )}
    </div>
  );
}

export default function Backgrounds() {
  const runtimeReady = useAccentBG();
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [candidate, setCandidate] = useState<AccentEffect | null>(null);
  const [validation, setValidation] = useState<{ ok: boolean; error: string | null; avgMs: number } | null>(null);
  const [publishMeta, setPublishMeta] = useState({ category: 'Overig', tier: 'free' as EffectTier, visible: true });
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'generate' | 'import' | 'convert'>('generate');
  const [imp, setImp] = useState({ key: '', name: '', desc: '', dark: false, perf: 2 as 1 | 2 | 3, code: '' });
  const [convHtml, setConvHtml] = useState('');
  const [converting, setConverting] = useState(false);
  const [accentHex, setAccentHex] = useState('#c98a1a');
  const [catalog, setCatalog] = useState<AccentEffect[]>([]);
  const [busy, setBusy] = useState(false);
  const [thumbBusy, setThumbBusy] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<{ ok: number; failed: number } | null>(null);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const previewRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const mountedRec = useRef<unknown>(null);

  useEffect(() => { effectsApi.list().then((r) => setCatalog(r.effects)).catch(() => {}); }, []);

  useEffect(() => {
    if (!runtimeReady || !candidate || !window.AccentBG || !previewRef.current) return;
    const AccentBG = window.AccentBG;
    // Unmount any previously mounted preview before creating a new one
    if (mountedRec.current) {
      AccentBG.unmount?.(mountedRec.current);
      mountedRec.current = null;
    }
    AccentBG.setAccent(accentHex);
    const v = AccentBG.validate(candidate);
    setValidation(v);
    if (!v.ok) return;
    AccentBG.register(candidate);
    previewRef.current.innerHTML = '';
    const stage = document.createElement('div');
    stage.style.cssText = 'position:relative;width:100%;aspect-ratio:16/10;overflow:hidden;border-radius:8px;background:#eae6dc';
    previewRef.current.appendChild(stage);
    const rec = AccentBG.mount(stage, candidate.key);
    rec?.inst?.start?.();
    mountedRec.current = rec;
    return () => {
      if (window.AccentBG && mountedRec.current) {
        window.AccentBG.unmount?.(mountedRec.current);
        mountedRec.current = null;
      }
    };
  }, [runtimeReady, candidate, accentHex]);

  async function generate(iteratePrompt?: string) {
    const text = iteratePrompt ?? prompt;
    if (!text.trim()) return;
    setGenerating(true); setError(null);
    try {
      const r = await effectsApi.generate(text, iteratePrompt ? candidate?.code : undefined);
      setCandidate(r.effect);
      setPublishMeta({ category: r.effect.category || 'Overig', tier: r.effect.tier || 'free', visible: true });
    } catch (e: unknown) {
      const err = e as { body?: { error?: string }; message?: string };
      setError(err?.body?.error || err?.message || 'Generatie mislukt');
    } finally {
      setGenerating(false);
    }
  }

  function renderThumbnail(container?: HTMLElement | null): string {
    const src = (container ?? previewRef.current)?.querySelector('canvas') as HTMLCanvasElement | null;
    const W = 640, H = Math.round(W * 10 / 16);
    const out = document.createElement('canvas'); out.width = W; out.height = H;
    const ctx = out.getContext('2d')!;
    if (src) ctx.drawImage(src, 0, 0, W, H);
    return out.toDataURL('image/png');
  }

  async function publish() {
    if (!candidate) return;
    setBusy(true); setError(null);
    try {
      const thumb = renderThumbnail();
      const rec: AccentEffect = {
        ...candidate,
        thumb,
        category: publishMeta.category || 'Overig',
        tier: publishMeta.tier,
        visible: publishMeta.visible,
      };
      await effectsApi.publish(rec);
      const r = await effectsApi.list();
      setCatalog(r.effects);
      setCandidate(null); setValidation(null); setPrompt('');
      setImp({ key: '', name: '', desc: '', dark: false, perf: 2, code: '' });
      setConvHtml('');
    } catch (e: unknown) {
      const err = e as { body?: { error?: string }; message?: string };
      setError(err?.body?.error || err?.message || 'Publiceren mislukt');
    } finally {
      setBusy(false);
    }
  }

  function loadImport() {
    setError(null);
    const rec = {
      key: imp.key.trim(),
      name: imp.name.trim(),
      desc: imp.desc.trim(),
      code: imp.code,
      dark: imp.dark,
      perf: imp.perf,
      thumb: '',
      createdAt: '',
      category: 'Overig',
      visible: true,
      tier: 'free' as EffectTier,
    };
    const errs: string[] = [];
    if (!/^[a-z0-9-]{1,36}$/.test(rec.key)) errs.push('key moet een slug zijn (a-z, 0-9, koppeltekens, max 36)');
    if (!rec.name) errs.push('name is verplicht');
    if (!/return\s+function/.test(rec.code)) errs.push('code moet eindigen met "return function(t){...}"');
    if (errs.length) { setError(errs.join(', ')); return; }
    setCandidate(rec as AccentEffect);
    setPublishMeta({ category: 'Overig', tier: 'free', visible: true });
  }

  async function convert() {
    if (!convHtml.trim()) return;
    setConverting(true); setError(null);
    try {
      const r = await effectsApi.convert(convHtml);
      setCandidate(r.effect);
      setPublishMeta({ category: r.effect.category || 'Overig', tier: r.effect.tier || 'free', visible: true });
    } catch (e: unknown) {
      const err = e as { body?: { error?: string }; message?: string };
      setError(err?.body?.error || err?.message || 'Conversie mislukt');
    } finally {
      setConverting(false);
    }
  }

  function switchMode(m: 'generate' | 'import' | 'convert') {
    setMode(m);
    setCandidate(null);
    setValidation(null);
    setError(null);
    setConvHtml('');
  }

  async function toggleVisible(e: AccentEffect) {
    await effectsApi.update(e.key, { visible: !e.visible });
    setCatalog((c) => c.map((x) => (x.key === e.key ? { ...x, visible: !x.visible } : x)));
  }
  async function changeTier(e: AccentEffect, tier: EffectTier) {
    await effectsApi.update(e.key, { tier });
    setCatalog((c) => c.map((x) => (x.key === e.key ? { ...x, tier } : x)));
  }
  async function remove(e: AccentEffect) {
    if (!confirm(`"${e.name}" verwijderen?`)) return;
    await effectsApi.remove(e.key);
    setCatalog((c) => c.filter((x) => x.key !== e.key));
  }
  async function syncNow() {
    setBusy(true);
    try { const r = await effectsApi.sync(); setSyncResult(r); }
    finally { setBusy(false); }
  }

  async function generateMissingThumbs() {
    const missing = catalog.filter((e) => !e.thumb);
    if (!missing.length || !window.AccentBG || !thumbRef.current) return;
    try {
      for (const e of missing) {
        setThumbBusy(e.key);
        window.AccentBG.setAccent(accentHex);
        window.AccentBG.register(e);
        if (thumbRef.current) thumbRef.current.innerHTML = '';
        const stage = document.createElement('div');
        stage.style.cssText = 'position:relative;width:480px;height:300px;overflow:hidden';
        thumbRef.current?.appendChild(stage);
        const rec = window.AccentBG.mount(stage, e.key);
        rec?.inst?.start?.();
        await new Promise((r) => setTimeout(r, 350));
        const thumb = renderThumbnail(thumbRef.current);
        if (rec) window.AccentBG.unmount?.(rec);
        if (thumb) { try { await effectsApi.update(e.key, { thumb }); } catch { /* skip */ } }
      }
      if (thumbRef.current) thumbRef.current.innerHTML = '';
      const r = await effectsApi.list();
      setCatalog(r.effects);
    } finally {
      setThumbBusy(null);
    }
  }

  const accents = window.AccentBG?.accents ?? [{ name: 'Oker', hex: '#c98a1a' }];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Achtergronden</h1>
        <button className="btn-secondary text-sm" onClick={generateMissingThumbs} disabled={!!thumbBusy || busy}>
          {thumbBusy ? `Thumbnail: ${thumbBusy}…` : 'Genereer ontbrekende thumbnails'}
        </button>
        <button className="btn-secondary text-sm flex items-center gap-2" onClick={syncNow} disabled={busy}>
          <UploadCloud className="w-4 h-4" /> Sync nu
        </button>
      </div>

      <section className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
        <div className="flex gap-2 mb-1">
          <button
            className={mode === 'generate' ? 'btn-primary text-sm' : 'btn-secondary text-sm'}
            onClick={() => switchMode('generate')}
          >Genereren</button>
          <button
            className={mode === 'import' ? 'btn-primary text-sm' : 'btn-secondary text-sm'}
            onClick={() => switchMode('import')}
          >Importeren</button>
          <button
            className={mode === 'convert' ? 'btn-primary text-sm' : 'btn-secondary text-sm'}
            onClick={() => switchMode('convert')}
          >Uit HTML</button>
        </div>
        {mode === 'generate' && (
          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder='bijv. "trage diagonale regen van accent-strepen"'
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') generate(); }}
            />
            <button className="btn-primary flex items-center gap-2" onClick={() => generate()} disabled={generating}>
              <Wand2 className="w-4 h-4" /> {generating ? 'Genereren…' : 'Genereer'}
            </button>
          </div>
        )}
        {mode === 'import' && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input className="input" placeholder="key (bijv. sidescroller)" value={imp.key}
                onChange={(e) => setImp({ ...imp, key: e.target.value })} />
              <input className="input" placeholder="Naam" value={imp.name}
                onChange={(e) => setImp({ ...imp, name: e.target.value })} />
            </div>
            <input className="input w-full" placeholder="Korte omschrijving" value={imp.desc}
              onChange={(e) => setImp({ ...imp, desc: e.target.value })} />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={imp.dark}
                  onChange={(e) => setImp({ ...imp, dark: e.target.checked })} /> dark (lichte herotekst)
              </label>
              <label className="flex items-center gap-2 text-sm">perf
                <select className="input" value={imp.perf}
                  onChange={(e) => setImp({ ...imp, perf: Number(e.target.value) as 1 | 2 | 3 })}>
                  <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
                </select>
              </label>
            </div>
            <textarea
              className="input w-full font-mono text-xs"
              style={{ minHeight: 180 }}
              placeholder="Plak hier het code-lichaam: ... return function(t){ const {w,h,ctx}=getSt(); ... }"
              value={imp.code}
              onChange={(e) => setImp({ ...imp, code: e.target.value })}
            />
            <button className="btn-primary" onClick={loadImport}>Laad in preview</button>
          </div>
        )}
        {mode === 'convert' && (
          <div className="space-y-2">
            <textarea
              className="input w-full font-mono text-xs"
              style={{ minHeight: 180 }}
              placeholder="Plak hier de hele claude.ai/design standalone HTML…"
              value={convHtml}
              onChange={(e) => setConvHtml(e.target.value)}
            />
            <button className="btn-primary flex items-center gap-2" onClick={convert} disabled={converting}>
              {converting ? 'Converteren…' : 'Converteer'}
            </button>
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {candidate && (
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <div ref={previewRef} />
              <div className="flex items-center gap-2 mt-3">
                {accents.map((a) => (
                  <button
                    key={a.hex}
                    title={a.name}
                    onClick={() => setAccentHex(a.hex)}
                    className="w-7 h-7 rounded-full border-2"
                    style={{ background: a.hex, borderColor: accentHex === a.hex ? '#16130f' : 'transparent' }}
                  />
                ))}
              </div>
              {validation && (
                <p className={`text-xs mt-2 ${validation.ok ? 'text-green-600' : 'text-red-600'}`}>
                  {validation.ok ? `Valide · ${validation.avgMs.toFixed(1)} ms/frame` : `Afgekeurd: ${validation.error}`}
                </p>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <p className="font-medium text-gray-900">{candidate.name}</p>
                <p className="text-sm text-gray-500">{candidate.desc}</p>
                <p className="text-xs text-gray-400 mt-1 font-mono">key: {candidate.key} · perf {candidate.perf} · {candidate.dark ? 'dark' : 'light'}</p>
                <details className="text-xs">
                  <summary className="cursor-pointer text-gray-500">Toon code</summary>
                  <pre className="mt-2 max-h-48 overflow-auto bg-gray-50 border border-gray-200 rounded p-2 font-mono whitespace-pre-wrap">{candidate.code}</pre>
                </details>
              </div>
              {mode === 'generate' && (
                <div className="flex flex-wrap gap-2">
                  {['donkerder', 'langzamer', 'minder druk', 'kleurrijker'].map((tweak) => (
                    <button key={tweak} className="btn-secondary text-xs" onClick={() => generate(tweak)} disabled={generating}>
                      <RefreshCw className="w-3 h-3 inline mr-1" />{tweak}
                    </button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <input className="input" value={publishMeta.category}
                  onChange={(e) => setPublishMeta({ ...publishMeta, category: e.target.value })} placeholder="Categorie" />
                <select className="input" value={publishMeta.tier}
                  onChange={(e) => setPublishMeta({ ...publishMeta, tier: e.target.value as EffectTier })}>
                  {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={publishMeta.visible}
                  onChange={(e) => setPublishMeta({ ...publishMeta, visible: e.target.checked })} />
                Direct zichtbaar voor tenants
              </label>
              <button className="btn-primary w-full" onClick={publish} disabled={busy || !validation?.ok}>
                Publiceren
              </button>
            </div>
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Catalogus ({catalog.length})</h2>
          <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden">
            <button title="Rasterweergave" onClick={() => setView('grid')}
              className={`p-1.5 ${view === 'grid' ? 'bg-brand-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button title="Lijstweergave" onClick={() => setView('list')}
              className={`p-1.5 ${view === 'list' ? 'bg-brand-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {view === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {catalog.map((e) => (
              <div key={e.key} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {e.thumb
                  ? <img src={e.thumb} alt={e.name} className="w-full aspect-[16/10] object-cover" />
                  : <div className="w-full aspect-[16/10] bg-gray-100" />}
                <div className="p-2 space-y-1.5">
                  <span className="block text-sm font-medium text-gray-900 truncate">{e.name}</span>
                  <EffectPills e={e} />
                  <div className="flex items-center gap-1">
                    <select className="input text-xs flex-1 py-1" value={e.tier}
                      onChange={(ev) => changeTier(e, ev.target.value as EffectTier)}>
                      {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button title={e.visible ? 'Zichtbaar' : 'Verborgen'} onClick={() => toggleVisible(e)}
                      className={`p-1.5 rounded ${e.visible ? 'text-green-600' : 'text-gray-400'}`}>
                      {e.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button title="Verwijderen" onClick={() => remove(e)} className="p-1.5 rounded text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {catalog.map((e) => (
              <div key={e.key} className="flex items-center gap-3 p-2">
                {e.thumb
                  ? <img src={e.thumb} alt={e.name} className="w-20 aspect-[16/10] object-cover rounded shrink-0" />
                  : <div className="w-20 aspect-[16/10] bg-gray-100 rounded shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900 truncate">{e.name}</span>
                    <EffectPills e={e} />
                  </div>
                  <p className="text-xs text-gray-500 truncate">{e.desc}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <select className="input text-xs py-1" value={e.tier}
                    onChange={(ev) => changeTier(e, ev.target.value as EffectTier)}>
                    {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <button title={e.visible ? 'Zichtbaar' : 'Verborgen'} onClick={() => toggleVisible(e)}
                    className={`p-1.5 rounded ${e.visible ? 'text-green-600' : 'text-gray-400'}`}>
                    {e.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button title="Verwijderen" onClick={() => remove(e)} className="p-1.5 rounded text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {syncResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSyncResult(null)}>
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-6 max-w-sm w-full mx-4" onClick={(ev) => ev.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Sync voltooid</h3>
            <p className="text-sm text-gray-600">
              {syncResult.ok} {syncResult.ok === 1 ? 'tenant' : 'tenants'} bijgewerkt
              {syncResult.failed > 0 ? `, ${syncResult.failed} mislukt` : ', geen fouten'}.
            </p>
            {syncResult.failed > 0 && (
              <p className="text-xs text-red-600 mt-2">Bekijk de server-logs voor de mislukte tenants.</p>
            )}
            <div className="mt-5 flex justify-end">
              <button className="btn-primary text-sm" onClick={() => setSyncResult(null)}>Sluiten</button>
            </div>
          </div>
        </div>
      )}

      {/* Altijd-aanwezige stage voor thumbnail-generatie: in-beeld (zodat de IntersectionObserver
          het effect laat lopen) maar onzichtbaar en achter alles. Werkt zonder actieve kandidaat. */}
      <div ref={thumbRef} aria-hidden="true" style={{ position: 'fixed', left: 0, top: 0, width: 480, height: 300, pointerEvents: 'none', opacity: 0, zIndex: -1 }} />
    </div>
  );
}
